import { datatableSearchSchema } from '@/lib/utils/datatableSearchSchema'
import { stringifyValues } from '@/lib/utils/queries'
import { getApi } from '@/stores/api'
import { EXPORT_JOB_STATUS } from '@repo/constants'
import { toastClassName } from '@repo/ui/lib/constants'
import { Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

/**
 * The export-jobs queue is shared by every module that offers background exports. Each module
 * mounts its own `/exports` router (scoped to its export types) and its own "My Exports" page; these
 * hooks take the module so the API path, the page link and the translation namespace follow it.
 */
export type ExportsModule = 'crt' | 'credit'

const MY_EXPORTS_PATH = {
  crt: '/crt/my-exports',
  credit: '/credit/my-exports',
} as const satisfies Record<ExportsModule, string>

export const myExportsSearchSchema = datatableSearchSchema

export type MyExportsSearch = z.infer<typeof myExportsSearchSchema>

// The two routers expose the same contract, so the client types unify; branch on the module only to
// pick the mount point.
function getExportsApi(module: ExportsModule) {
  const api = getApi()
  return module === 'crt' ? api.crt.exports : api.credit.exports
}

async function getMyExports(module: ExportsModule, queries: MyExportsSearch) {
  const res = await getExportsApi(module).$get({
    query: stringifyValues(queries),
  })

  const { exports, count } = await res.json()
  return { exports, count }
}

export type MyExports = Awaited<ReturnType<typeof getMyExports>>

export function useMyExports(module: ExportsModule, queries: MyExportsSearch) {
  return useQuery({
    queryKey: ['my-exports', module, queries],
    queryFn: () => getMyExports(module, queries),
    // Keep the shared list fresh while any export is still being generated.
    refetchInterval: (query) => {
      const hasActiveJob = query.state.data?.exports.some(
        (job) =>
          job.status === EXPORT_JOB_STATUS.PENDING || job.status === EXPORT_JOB_STATUS.PROCESSING,
      )
      return hasActiveJob ? 5_000 : false
    },
    refetchIntervalInBackground: true,
  })
}

async function getExportDownloadUrl(module: ExportsModule, jobId: number) {
  const res = await getExportsApi(module)[':jobId'].download.$get({
    param: { jobId: String(jobId) },
  })
  return await res.json()
}

/** Resolves a completed export job's presigned URL and triggers a browser download. */
export function useDownloadExport(module: ExportsModule) {
  const { t } = useTranslation('common')
  const [isDownloading, setIsDownloading] = useState(false)

  const download = async (jobId: number, fileName: string) => {
    setIsDownloading(true)

    try {
      const { url } = await getExportDownloadUrl(module, jobId)
      const res = await fetch(url, { credentials: 'omit' })
      // fetch only rejects on network failure, so a non-2xx S3 response (e.g. a 500/503 with an XML
      // error body, or a 404 NoSuchKey when the object was reclaimed after the row) would otherwise
      // be saved as a corrupt file under the export's name. Throw so the catch surfaces the toast.
      if (!res.ok) throw new Error(`Export download failed: ${res.status} ${res.statusText}`)
      const blob = await res.blob()
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = fileName
      document.body.appendChild(a)
      a.click()
      URL.revokeObjectURL(a.href)
      a.remove()
    } catch {
      toast.error(t('toast.error'), {
        description: t('export.downloadFailed'),
        className: toastClassName,
      })
    } finally {
      setIsDownloading(false)
    }
  }

  return { download, isDownloading }
}

/** Minimal shape shared by every feature's created-export-job response. */
export type CreatedExportJob = { id: number; status: string; fileName: string | null }

/**
 * Shared handler for a freshly created export job, used by every feature's export trigger. On a
 * cache hit (the backend returned an already-completed export) it downloads immediately; otherwise
 * it shows a single toast confirming the export has started, with a link to the module's "My
 * Exports" page.
 */
export function useHandleExportJobCreated(module: ExportsModule) {
  const { t } = useTranslation('common')
  const { download } = useDownloadExport(module)

  return (job: CreatedExportJob) => {
    if (job.status === EXPORT_JOB_STATUS.COMPLETED) {
      toast.success(t('export.alreadyAvailable'), {
        description: t('export.alreadyAvailableDescription'),
        className: toastClassName,
      })
      download(job.id, job.fileName ?? 'export.csv')
      return
    }

    const toastId = toast.success(t('export.started'), {
      description: (
        <div className="flex flex-col items-start gap-1">
          <span>{t('export.startedDescription')}</span>
          <Link
            to={MY_EXPORTS_PATH[module]}
            className="font-medium underline underline-offset-2"
            onClick={() => toast.dismiss(toastId)}
          >
            {t('export.goToMyExports')}
          </Link>
        </div>
      ),
      className: toastClassName,
      // The global Toaster renders `closeButton` as an invisible full-size overlay (click anywhere
      // to dismiss), which would swallow the link's click. Disable it for this toast so the
      // "Go to My Exports" link is clickable, and give the user time to act on it.
      closeButton: false,
      duration: 10_000,
    })
  }
}
